import { useEffect, useRef, useState } from 'react';
import { create } from 'zustand';

/**
 * Avisos e perguntas da aplicação.
 *
 * Existe para não haver um único `alert()`, `confirm()` ou `prompt()` no
 * produto. Além de feios e não estilizáveis, esses diálogos bloqueiam o
 * thread do browser — e um thread bloqueado é um WebSocket que não processa
 * eventos: durante um `alert()` o gateway acumula mensagens, os indicadores
 * de escrita congelam e a voz fica à espera. Um aviso em HTML não faz nada
 * disso, e deixa a conversa legível por trás.
 */

type NoticeKind = 'info' | 'error';

type Notice = {
  id: number;
  text: string;
  kind: NoticeKind;
};

type Question = {
  id: number;
  title: string;
  label: string;
  placeholder: string;
  confirm: string;
  maxLength: number;
  initial: string;
  multiline: boolean;
  /** Pergunta de sim/não: sem campo, e o botão pode ser de destruir. */
  kind: 'text' | 'confirm';
  danger: boolean;
  body: string | null;
  settle: (answer: string | null) => void;
};

type OverlayState = {
  notices: Notice[];
  /** Fila, não um só: dois cliques seguidos não se devem comer um ao outro. */
  questions: Question[];
};

const useOverlays = create<OverlayState>(() => ({ notices: [], questions: [] }));

/** Quanto tempo fica no ecrã. Um erro merece mais tempo de leitura. */
const LIFETIME: Record<NoticeKind, number> = { info: 5000, error: 8000 };

let counter = 0;

/**
 * Aviso passageiro, no canto. Chamável de qualquer sítio — não precisa de
 * contexto nem de estar dentro de um componente.
 */
export function notify(text: string, kind: NoticeKind = 'info'): void {
  const id = ++counter;
  useOverlays.setState((s) => ({ notices: [...s.notices, { id, text, kind }] }));
  setTimeout(() => dismiss(id), LIFETIME[kind]);
}

function dismiss(id: number): void {
  useOverlays.setState((s) => ({ notices: s.notices.filter((n) => n.id !== id) }));
}

/**
 * Pergunta de uma linha. Devolve o texto escrito, já sem espaços nas pontas,
 * ou `null` se o utilizador desistiu — a mesma forma que o `prompt()` tinha,
 * para o código que chama ficar igual.
 */
export function ask(q: {
  title: string;
  label: string;
  placeholder?: string;
  confirm?: string;
  maxLength?: number;
  /** Valor de partida — editar é começar com o que lá estava. */
  initial?: string;
  multiline?: boolean;
}): Promise<string | null> {
  return new Promise((settle) => {
    const question: Question = {
      id: ++counter,
      title: q.title,
      label: q.label,
      placeholder: q.placeholder ?? '',
      confirm: q.confirm ?? 'Confirmar',
      maxLength: q.maxLength ?? 100,
      initial: q.initial ?? '',
      multiline: q.multiline ?? false,
      kind: 'text',
      danger: false,
      body: null,
      settle,
    };
    useOverlays.setState((s) => ({ questions: [...s.questions, question] }));
  });
}

/**
 * Sim ou não. O que era o `confirm()` — e, tal como o `prompt()`, o nativo
 * não deixava dizer o que acontece nem pôr o nome certo no botão. Apagar um
 * canal e sair de um canal não são "OK".
 */
export function confirm(q: {
  title: string;
  body?: string;
  confirm?: string;
  danger?: boolean;
}): Promise<boolean> {
  return new Promise((resolve) => {
    const question: Question = {
      id: ++counter,
      title: q.title,
      label: '',
      placeholder: '',
      confirm: q.confirm ?? 'Confirmar',
      maxLength: 0,
      initial: '',
      multiline: false,
      kind: 'confirm',
      danger: q.danger ?? false,
      body: q.body ?? null,
      settle: (answer) => resolve(answer !== null),
    };
    useOverlays.setState((s) => ({ questions: [...s.questions, question] }));
  });
}

/** Monta-se uma vez, no topo da árvore. */
export function Overlays(): React.ReactElement {
  const notices = useOverlays((s) => s.notices);
  const question = useOverlays((s) => s.questions[0]);

  return (
    <>
      {/* `role="status"` com `polite`: é lido depois do que o utilizador está
          a fazer, sem lhe cortar a palavra como faria um `alert`. */}
      <div className="notices" role="status" aria-live="polite">
        {notices.map((n) => (
          <button
            key={n.id}
            className={`notice ${n.kind}`}
            title="Dispensar"
            onClick={() => dismiss(n.id)}
          >
            {n.text}
          </button>
        ))}
      </div>
      {question && <AskDialog key={question.id} question={question} />}
    </>
  );
}

/**
 * `<dialog>` nativo aberto com `showModal()`, não uma div com z-index: o
 * foco preso lá dentro, o Escape e o fundo inerte vêm do browser, prontos e
 * acessíveis. O que o `prompt()` dá sem nada disto ser nosso.
 */
function AskDialog({ question }: { question: Question }): React.ReactElement {
  const dialog = useRef<HTMLDialogElement>(null);
  const [value, setValue] = useState(question.initial);

  useEffect(() => {
    dialog.current?.showModal();
  }, []);

  function close(answer: string | null): void {
    useOverlays.setState((s) => ({ questions: s.questions.filter((q) => q.id !== question.id) }));
    question.settle(answer);
  }

  const answer = value.trim();
  // Numa pergunta de sim/não o `settle` só olha para null vs não-null.
  const confirmed = question.kind === 'confirm' ? 'sim' : answer;
  const ready = question.kind === 'confirm' || answer.length > 0;

  return (
    <dialog
      ref={dialog}
      className="ask"
      aria-labelledby={`ask-${question.id}`}
      // Escape — o browser emite `cancel` e fecha; só temos de desistir.
      onCancel={() => close(null)}
      // Clique fora. O alvo só é o próprio `<dialog>` quando o rato acerta no
      // ::backdrop; dentro do cartão o alvo é sempre um filho.
      onClick={(e) => {
        if (e.target === dialog.current) close(null);
      }}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (ready) close(confirmed);
        }}
      >
        <h2 id={`ask-${question.id}`}>{question.title}</h2>

        {question.body && <p className="ask-body">{question.body}</p>}

        {question.kind === 'text' &&
          (question.multiline ? (
            <label>
              {question.label}
              <textarea
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder={question.placeholder}
                maxLength={question.maxLength}
                rows={4}
                autoFocus
                // Enter envia, Shift+Enter faz parágrafo — como no composer,
                // que é onde esta mensagem foi escrita da primeira vez.
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    if (ready) close(confirmed);
                  }
                }}
              />
            </label>
          ) : (
            <label>
              {question.label}
              <input
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder={question.placeholder}
                maxLength={question.maxLength}
                autoFocus
              />
            </label>
          ))}

        <div className="ask-actions">
          <button type="button" className="link" onClick={() => close(null)}>
            Cancelar
          </button>
          <button
            type="submit"
            className={question.danger ? 'danger' : ''}
            disabled={!ready}
            autoFocus={question.kind === 'confirm'}
          >
            {question.confirm}
          </button>
        </div>
      </form>
    </dialog>
  );
}
